import { Field, Form, Formik, FormikConfig } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { styled } from 'styled-components';
import { Box } from '@mui/material';

import { DefaultButton } from 'common/components/Button/DefaultButton';
import { TextField } from 'common/components/FormField';
import { Button } from 'common/components/Button';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';

export type TFormValues = {
    title: string;
    description?: string;
};

interface IProps extends FormikConfig<TFormValues> {
    className?: string;
    isDeleting?: boolean;
    onClose?(): void;
    onDelete?(): void;
}

const validationSchema = Yup.object().shape({
    title: Yup.string().required(),
    description: Yup.string().optional(),
});

const BaseGroupForm = ({
    className,
    isDeleting,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();

    return (
        <Formik validationSchema={validationSchema} {...other}>
            {({ submitForm, isSubmitting, dirty }) => (
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting && !isDeleting}
                        onConfirm={submitForm}
                    />

                    <Form className={className}>
                        <Field
                            component={TextField}
                            label={translate('name')}
                            name="title"
                        />
                        <Field
                            multiline
                            component={TextField}
                            label={translate('description')}
                            name="description"
                        />

                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <Button
                                color="primary"
                                disabled={
                                    isSubmitting || (!dirty && !isSubmitting)
                                }
                                loading={isSubmitting}
                                type="submit"
                                variant="contained"
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    e.preventDefault();

                                    submitForm();
                                }}
                            >
                                {translate('save')}
                            </Button>

                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}

                            {!!onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};

export const DevelopmentItemGroupForm = styled(BaseGroupForm)<IProps>`
    ${DefaultButton} {
        margin-top: ${({ theme }) => theme.spacing(2)};
    }

    textarea {
        min-height: ${({ theme }) => theme.spacing(10)};
    }
`;
